import React, { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { visitIQCategories, visitIQGeoFilters } from '~/constants';
import { useAppContext } from '~/contexts/AppContext';
import { useDebounce } from '~/hooks/useDebounce';
import i18n from '~/locale';
import { Permission, PulseNode, usePulseNodesQuery } from '~/types/api.graphql';
import { ExtendedPulseNode } from '~/views/audiences/NewPulseAudience/types';

import { useSessionContext } from './SessionContext';

const MAX_TOPICS_LIMIT = 15;
const MAX_TOPICS_LIMIT_FOR_ADMINS = 100;

interface SignalContextProps {
  displayCategories: ExtendedPulseNode[];
  loadingCategories: boolean;
  selectedCategory: SelectedCategory;
  selectedTopics: PulseNode[];
  setSelectedTopics: (topics: PulseNode[]) => void;
  setSelectedCategory: (category: SelectedCategory) => void;
  toggleTopic: (topic: PulseNode) => void;
  clearSignalsLocalStorage: () => void;
  selectedAmountOfSignals: number;
  getSelectedTopicsCount: (category: PulseNode) => number;
  loadNextPage: () => Promise<void>;
  hasMorePages: boolean;
  loadingNextPage: boolean;
  setSearchQuery: (query: string) => void;
  searchQuery: string;
  isTopicLimitReached: boolean;
  amountOfBubblesInLevel: number;
  resetState: () => void;
  isAtTopicLevel: boolean;
  debouncedSearchQuery: string;
  simpleLoadingIndicator: boolean;
  setSimpleLoadingIndicator: (indicator: boolean) => void;
  abortCurrentRequests: () => void;
  maxTopicsLimit: number;
}

export const ROOT_CATEGORY_ID = 'root';
interface SignalProviderProps {
  children: ReactNode;
}

const emptyPulseNode: ExtendedPulseNode = {
  isShimmer: true,
  signalCount: 0,
  name: '',
  paths: [],
  id: '',
};

const SignalContext = createContext<SignalContextProps>({
  displayCategories: [],
  loadingCategories: false,
  selectedCategory: null,
  selectedTopics: [],
  setSelectedCategory: null,
  toggleTopic: null,
  clearSignalsLocalStorage: null,
  selectedAmountOfSignals: 0,
  getSelectedTopicsCount: null,
  loadNextPage: () => null,
  hasMorePages: false,
  loadingNextPage: false,
  setSearchQuery: null,
  searchQuery: '',
  setSelectedTopics: null,
  isTopicLimitReached: false,
  amountOfBubblesInLevel: 0,
  resetState: null,
  isAtTopicLevel: false,
  debouncedSearchQuery: '',
  simpleLoadingIndicator: false,
  setSimpleLoadingIndicator: null,
  abortCurrentRequests: null,
  maxTopicsLimit: MAX_TOPICS_LIMIT,
});

export const PAGE_ITEMS_COUNT = 55;
export const MAX_LEVELS_OF_PATHS = 3;

type SelectedCategory = Omit<PulseNode, 'signalCount'> | null;

export const SignalProvider: FC<SignalProviderProps> = ({ children }) => {
  const { me, isOrgAdmin } = useSessionContext();
  const { showBottomToast } = useAppContext();
  const [categories, setCategories] = useState<ExtendedPulseNode[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>(null);
  const [selectedTopics, setSelectedTopics] = useState<PulseNode[]>([]);
  const [isInitialized, setInitialized] = useState(false);
  const [endCursor, setEndCursor] = useState('');
  const [simpleLoadingIndicator, setSimpleLoadingIndicator] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [amountOfBubblesInLevel, setAmountOfBubblesInLevel] = useState(0);
  const { audienceId: parentAudienceId } = useParams();
  const categoriesLocalStorage = parentAudienceId ? `${visitIQCategories}-${parentAudienceId}` : visitIQCategories;

  const maxTopicsLimit = useMemo(() => {
    const hasSuperOwnerRole = me?.userRoles?.some((userRole) =>
      userRole.assignedRole?.permissions?.includes(Permission.SuperOwner),
    );

    return isOrgAdmin || hasSuperOwnerRole ? MAX_TOPICS_LIMIT_FOR_ADMINS : MAX_TOPICS_LIMIT;
  }, [me, isOrgAdmin]);

  // Create a single AbortController that we'll expose
  const abortControllerRef = useRef<AbortController>(new AbortController());

  // Export a function to abort current requests and create a new controller
  const abortCurrentRequests = useCallback(() => {
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // Create new controller for future requests
      abortControllerRef.current = new AbortController();

      // Reset pagination state to force new queries
      setEndCursor('');
      setHasMorePages(true);
      setSimpleLoadingIndicator(false);
      setLoadingNextPage(false);
    }
  }, []);

  const debouncedSearchQuery = useDebounce(searchQuery, 850) as string;
  // Use GraphQL query for pagination
  const { fetchMore, loading: loadingCategories } = usePulseNodesQuery({
    variables: {
      first: PAGE_ITEMS_COUNT,
      parentPaths: debouncedSearchQuery ? [] : selectedCategory ? selectedCategory.paths : [],
      searchQuery: debouncedSearchQuery,
    },
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
    skip: endCursor !== '',
    context: {
      fetchOptions: {
        signal: abortControllerRef.current.signal,
      },
    },
    onCompleted: (data) => {
      setAmountOfBubblesInLevel(data?.pulseNodes?.totalCount || 0);
      const newNodes = data?.pulseNodes?.nodes || [];
      // Only update categories if we have real nodes to show
      if (newNodes.length > 0) {
        setCategories(newNodes);
        setHasMorePages(data?.pulseNodes?.pageInfo?.hasNextPage || false);
        setEndCursor(data?.pulseNodes?.pageInfo?.endCursor || '');
      }
    },
  });

  // Improve the shimmer bubble creation function
  const createShimmerBubbles = useCallback(
    (count: number, prefix = '') => {
      return Array.from({ length: count }).map((_, index) => ({
        ...emptyPulseNode,
        id: `shimmer-${prefix}${index}-${selectedCategory?.id || ''}-${Math.random().toString(36).substring(2, 9)}`,
      }));
    },
    [selectedCategory],
  );

  useEffect(() => {
    // Load saved state
    const savedState = JSON.parse(localStorage.getItem(categoriesLocalStorage) || '{}');
    if (savedState.selectedCategory) {
      setSelectedCategory(savedState.selectedCategory as SelectedCategory);
    }
    if (savedState.selectedTopics) {
      setSelectedTopics(savedState.selectedTopics as PulseNode[]);
    }

    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    localStorage.setItem(
      categoriesLocalStorage,
      JSON.stringify({
        selectedCategory,
        selectedTopics,
      }),
    );
  }, [selectedCategory, selectedTopics]);

  const resetState = useCallback(() => {
    setLoadingNextPage(false);
    setSimpleLoadingIndicator(false);
    setEndCursor('');
    setHasMorePages(false);
    setCategories([]);
    setAmountOfBubblesInLevel(0);
  }, []);

  useEffect(() => {
    resetState();
    if (debouncedSearchQuery) {
      setSelectedCategory(null);
    }
  }, [debouncedSearchQuery, selectedCategory]);

  // Ensure loadNextPage can properly re-trigger when needed
  const loadNextPage = useCallback(async () => {
    if (!hasMorePages || loadingNextPage || loadingCategories) return;

    // Add a uniqueness factor to ensure Apollo treats this as a new request
    const uniqueRequestId = Date.now().toString();

    setLoadingNextPage(true);
    try {
      const response = await fetchMore({
        variables: {
          first: PAGE_ITEMS_COUNT,
          after: endCursor,
          parentPaths: debouncedSearchQuery ? [] : selectedCategory ? selectedCategory.paths : [],
          searchQuery: debouncedSearchQuery,
          _requestId: uniqueRequestId, // Apollo ignores this but it makes the variables object unique
        },
        context: {
          fetchOptions: {
            signal: abortControllerRef.current.signal,
          },
        },
      });

      const newNodes = response.data?.pulseNodes?.nodes || [];
      // Only update if we have real nodes
      if (newNodes.length > 0) {
        setCategories((prev) => [...prev, ...newNodes]);
        setHasMorePages(response.data?.pulseNodes?.pageInfo?.hasNextPage || false);
        setEndCursor(response.data?.pulseNodes?.pageInfo?.endCursor || '');
      }
    } finally {
      setLoadingNextPage(false);
      setSimpleLoadingIndicator(false);
    }
  }, [endCursor, hasMorePages, loadingNextPage, loadingCategories, fetchMore, debouncedSearchQuery, selectedCategory]);

  const isTopicLimitReached = useMemo(() => {
    return selectedTopics.length >= maxTopicsLimit;
  }, [selectedTopics]);

  const toggleTopic = (topic: PulseNode) => {
    setSelectedTopics((prevTopics) => {
      const topicExists = prevTopics.some((t) => t.id === topic.id);

      // If topic exists, we're removing it, so that's always allowed
      if (topicExists) {
        return prevTopics.filter((t) => t.id !== topic.id);
      } else {
        // Only add if it's a leaf node (topic) and we haven't reached the limit
        // Topics have 3 path segments (e.g., ["Animal Lovers", "Dogs", "Dog Lovers"])
        if (topic.paths.length === MAX_LEVELS_OF_PATHS) {
          if (prevTopics.length >= maxTopicsLimit) {
            // Show toast notification when limit is reached
            showBottomToast(i18n.t('newPulseAudience.topicLimitReachedTitle'));
            return prevTopics; // Don't add the new topic
          }

          // Show toast notification when limit is reached
          if (prevTopics.length === maxTopicsLimit - 1) {
            showBottomToast(i18n.t('newPulseAudience.topicLimitReachedTitle'));
          }
          return [topic, ...prevTopics];
        }
        return prevTopics;
      }
    });
  };

  const getSelectedTopicsCount = (category: PulseNode): number => {
    // Count topics based on path hierarchy
    return selectedTopics.filter((topic) => {
      // Get the path length we need to compare
      // - Categories have 1 path segment
      // - Subcategories have 2 path segments
      // - Topics have 3 path segments
      const pathLength = category.paths.length;

      // Compare the paths up to the category's depth
      const topicPath = topic.paths.slice(0, pathLength).join('|');
      const categoryPath = category.paths.join('|');

      return topicPath === categoryPath;
    }).length;
  };

  const selectedAmountOfSignals = useMemo(() => {
    // Only count signals from fully selected topics (path length of 3)
    return selectedTopics
      .filter((topic) => topic.paths.length === MAX_LEVELS_OF_PATHS)
      .reduce((acc, topic) => acc + topic.signalCount, 0);
  }, [selectedTopics]);

  const clearSignalsLocalStorage = () => {
    const geoFiltersLocalStorage = parentAudienceId ? `${visitIQGeoFilters}-${parentAudienceId}` : visitIQGeoFilters;
    localStorage.removeItem(categoriesLocalStorage);
    localStorage.removeItem(geoFiltersLocalStorage);
    setSelectedCategory(null);
    setSelectedTopics([]);
  };

  const displayCategories = useMemo(() => {
    if (loadingCategories && categories.length === 0) {
      return createShimmerBubbles(PAGE_ITEMS_COUNT, 'initial-');
    }

    // Already have items but loading more
    if (loadingNextPage) {
      // Return existing categories plus shimmer placeholders at the end
      return [...categories, ...createShimmerBubbles(PAGE_ITEMS_COUNT, 'next-')];
    }

    // Normal state - just return the categories
    return categories;
  }, [categories, loadingCategories, loadingNextPage, createShimmerBubbles]);

  const isAtTopicLevel = useMemo(() => {
    return selectedCategory?.paths.length === MAX_LEVELS_OF_PATHS - 1;
  }, [selectedCategory]);

  return (
    <SignalContext.Provider
      value={{
        displayCategories,
        loadingCategories,
        selectedCategory,
        selectedTopics,
        selectedAmountOfSignals,
        setSelectedCategory,
        toggleTopic,
        clearSignalsLocalStorage,
        getSelectedTopicsCount,
        loadNextPage,
        hasMorePages,
        loadingNextPage,
        setSearchQuery,
        searchQuery,
        setSelectedTopics,
        isTopicLimitReached,
        amountOfBubblesInLevel,
        resetState,
        isAtTopicLevel,
        debouncedSearchQuery,
        simpleLoadingIndicator,
        setSimpleLoadingIndicator,
        abortCurrentRequests,
        maxTopicsLimit,
      }}
    >
      {children}
    </SignalContext.Provider>
  );
};

export const useSignalContext = (): SignalContextProps => {
  return React.useContext(SignalContext);
};
